// /app/frontend/src/components/FSBOHero.jsx
// Clean, focused hero section for DIY Sellers / FSBO
import { useNavigate } from "react-router-dom";
import { Home, ArrowRight, CheckCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const FSBOHero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative overflow-hidden bg-[#FAF9F6]" data-testid="fsbo-hero">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[80vh] py-12 lg:py-8">
          
          {/* Left Column - Content */}
          <div className="lg:col-span-5 space-y-6 lg:space-y-8 z-10">

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sell Your Home
                <span className="block text-[#E07C4D]">Yourself.</span>
                <span className="block text-[#1E3A5F]">Keep the Equity.</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-lg">
                The <strong>#1 Free FSBO Platform</strong> in Dallas-Fort Worth. 
                No agent fees. No commissions. Just you and your buyer.
              </p>
            </div>

            {/* COMING SOON - MLS Banner */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 shadow-lg">
              <div className="flex items-start gap-3">
                <span className="px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded uppercase tracking-wide flex-shrink-0">
                  Coming Soon
                </span>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    MLS Listings – Get Found on Zillow, Realtor.com, Redfin, Trulia, Homes.com & More!
                  </p>
                  <p className="text-blue-100 text-xs sm:text-sm mt-1">
                    Your listing syndicated to all major sites + local brokerage websites across DFW
                  </p>
                </div>
              </div>
            </div>

            {/* Value Props */}
            <div className="space-y-3">
              {[
                "100% FREE to list your home",
                "Save $15,000 - $50,000 in commissions",
                "Direct contact with serious buyers",
                "You control the entire process"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => navigate('/register?role=seller')}
                className="bg-gradient-to-r from-[#E07C4D] to-[#D16844] hover:from-[#D16844] hover:to-[#B55D35] text-white font-semibold py-6 px-8 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-lg"
                data-testid="hero-list-home-btn"
              >
                List My Home FREE
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <Button
                onClick={() => navigate('/register?role=buyer')}
                variant="outline"
                className="bg-white text-[#1E3A5F] border-2 border-[#1E3A5F] font-semibold py-6 px-8 rounded-full hover:bg-[#1E3A5F] hover:text-white transition-all duration-300 text-lg"
                data-testid="hero-browse-homes-btn"
              >
                Browse Homes
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {['MT', 'JK', 'SL', 'RG'].map((initials, idx) => (
                  <div 
                    key={idx}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                  >
                    {initials}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-600">Trusted by <strong>1000s</strong> of Texas homeowners</p>
              </div>
            </div>
          </div>

          {/* Right Column - Image */}
          <div className="lg:col-span-7 relative">
            <div className="relative h-[400px] sm:h-[500px] lg:h-[600px]">
              {/* Main Image */}
              <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/hero-its-sold.png"
                  alt="Happy homeowners celebrating successful FSBO home sale with Its-Sold sign"
                  className="w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
              </div>

              {/* Floating Stats Card - positioned at bottom right to not block sign */}
              <div className="absolute -bottom-6 -right-6 sm:bottom-8 sm:right-8 bg-white rounded-2xl shadow-xl p-5 border border-gray-100 max-w-[240px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">$25,000+</p>
                    <p className="text-xs text-gray-600">Average Savings</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500">vs. 6% agent commission</p>
              </div>

              {/* FSBO Badge - top left */}
              <div className="absolute top-6 left-6 bg-[#1E3A5F] text-white px-5 py-3 rounded-full shadow-lg">
                <p className="text-sm font-bold tracking-wide">FOR SALE BY OWNER</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="relative bg-[#1E3A5F] py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 text-white/90">
            <div className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              <span className="font-semibold">Est. 1998</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30" />
            <div className="flex items-center gap-2">
              <span className="text-yellow-400">★</span>
              <span className="font-semibold">A+ Rating</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30" />
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="font-semibold">100% FREE</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30" />
            <div className="flex items-center gap-2">
              <span className="font-semibold">No Agent Fees Ever</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-white/30" />
            <div className="flex items-center gap-2">
              <span className="font-semibold">Dallas-Fort Worth</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FSBOHero;
